import { useTranslations } from "../../store/translations/hooks";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TK } from "../../store/translations/translationKeys";
import TitledContainer from "../../components/TitledContainer";
import { DateRangeSelector, DateRangeSelectorState } from "../../components/DateRangeSelector";
import { useEntitiesResult } from "../../store/entities/hooks";
import { AppointmentChangeRecordData, AppointmentData, FeatureEnum, PermissionEnum } from "../../models";
import { useDispatch, useSelector } from "react-redux";
import { isLoadingEntities } from "../../store/entities/selectors";
import { Spinner } from "reactstrap";
import { useQueryParam } from "../../hooks/useQueryParam";
import { loadEntities } from "../../store/entities/actions";
import { getAppointmentChangesAsync, getAppointmentsAsync, getMyAppointmentsAsync } from "../../ajax/requests";
import { getMyUser, getFeatures } from "../../store/session/selectors";
import { AnalyticsHours } from "./AnalyticsHours";
import { AutocompleteTextField } from "../../components/Forms/fields";
import { AnalyticsRetention } from "./AnalyticsRetention";
import { AnalyticsCancellations } from "./AnalyticsCancellations";
import { AnalyticsOcupancy } from "./AnalyticsOcupancy";
import { AnalyticsSurveys } from "./AnalyticsSurveys";
import { AnalyticsGoogleReviews } from "./AnalyticsGoogleReviews";
import AppButton from '../../components/AppButton';
import styled from 'styled-components';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import BarChartIcon from '@material-ui/icons/BarChart';
import PeopleIcon from '@material-ui/icons/People';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import PieChartIcon from '@material-ui/icons/PieChart';
import PollIcon from '@material-ui/icons/Poll';
import StarIcon from '@material-ui/icons/Star';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { push } from 'connected-react-router';
import { paths } from '../../app/constants';
import './Analytics.scss';

type Tab = 'hours'|'count'|'retention'|'cancellations'|'ocupancy'|'surveys'|'googleReviews'

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    background: none;
    border: none;
    color: var(--brand-secondary);
    cursor: pointer;
    padding: 0;
    font-size: 0.9rem;
    margin-bottom: var(--spacer-S);
    &:hover { text-decoration: underline; }
`

interface CardDef {
    tab?: Tab;
    icon: React.ReactNode;
    titleKey: TK;
    descriptionKey: TK;
    hidden?: boolean;
    externalPath?: string;
}

export default () => {
    const t = useTranslations()
    const dispatch = useDispatch()

    const user = useSelector(getMyUser)
    const features = useSelector(getFeatures)

    const [showHome, setShowHome] = useState(() => !new URLSearchParams(document.location.search).get('tab'))
    const [needLoad, setNeedLoad] = useState(true)
    const [window, setWindow] = useState<DateRangeSelectorState>({from: new Date().toDateString(), to: new Date().toDateString(), type: 'day'})
    const [selectedWindow, setSelectedWindow] = useState<DateRangeSelectorState>(window)
    const [defaultIntervalType, setDefaultIntervalType] = useState<'day'|'month'|'range'>('month')
    const [defaultIntervalStartDay, setDefaultIntervalStartDay] = useState(new Date().toISOString().substr(0, 10))
    const [defaultIntervalEndDay, setDefaultIntervalEndDay] = useState(new Date().toISOString().substr(0, 10))
    const [defaultIntervalMonth, setDefaultIntervalMonth] = useState(new Date().toISOString().substr(0, 7))
    const [cancellations, setCancellations] = useState<AppointmentChangeRecordData[]>([])
    const loadedWindow = useRef<DateRangeSelectorState | null>(null)

    const canSeeAll = useMemo(() => user?.permissions.some(p => p === PermissionEnum.Administrator || p === PermissionEnum.Analytics), [user])

    const [tab, setTab] = useQueryParam<Tab>('tab', 'hours')

    const allAppointments = useEntitiesResult<AppointmentData>('appointments')
    const appointments = useMemo(() => canSeeAll
            ? allAppointments
            : allAppointments.filter(a => user?.professionalId && [a.professionalId, ...(a.additionalProfessionals?.map(p => p.id) ?? [])].includes(user.professionalId)),
        [allAppointments, canSeeAll, user?.professionalId])

    const isLoading = useSelector(isLoadingEntities('appointments'))

    useEffect(() => {
        if (tab === 'surveys' || tab === 'googleReviews') return
        const lw = loadedWindow.current
        if (!lw || lw.from !== selectedWindow.from || lw.to !== selectedWindow.to) {
            setNeedLoad(true)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tab changes are handled via selectTab/onValueChanged; this effect only reacts to window changes
    }, [selectedWindow])
    useEffect(() => {
        if (tab !== 'cancellations' || needLoad) return
        getAppointmentChangesAsync(window.from, window.to).then(setCancellations)
    }, [tab, window, needLoad])

    const triggerLoad = (w: DateRangeSelectorState) => {
        loadedWindow.current = w
        setSelectedWindow(w)
        dispatch(loadEntities(
            'appointments',
            () => canSeeAll ? getAppointmentsAsync(w.from, w.to) : getMyAppointmentsAsync(w.from, w.to),
            'NewResult',
            e => e.date >= w.from && e.date <= w.to,
        ))
        setWindow(w)
        setNeedLoad(false)
    }

    const handleGo = () => {
        if (isLoading) return
        triggerLoad(selectedWindow)
    }

    const setupRetention = (): DateRangeSelectorState => {
        const start = new Date()
        start.setMonth(start.getMonth() - 1)
        start.setDate(1)
        const from = start.toISOString().substring(0, 10)
        const to = `${start.getFullYear() + 1}-12-31`
        setDefaultIntervalType('range')
        setDefaultIntervalStartDay(from)
        setDefaultIntervalEndDay(to)
        return { from, to, type: 'range' }
    }

    const selectTab = (card: CardDef) => {
        if (card.externalPath) {
            dispatch(push(card.externalPath))
            return
        }
        if (!card.tab) return
        setShowHome(false)
        setTab(card.tab)
        if (card.tab === 'surveys' || card.tab === 'googleReviews') return
        const w = card.tab === 'retention' ? setupRetention() : selectedWindow
        triggerLoad(w)
    }

    const tabMap = useMemo<Record<string, Tab>>(() => ({
        [t(TK.hours)]: 'hours',
        [t(TK.count)]: 'count',
        [t(TK.retention)]: 'retention',
        [t(TK.cancellations)]: 'cancellations',
        [t(TK.ocupancy)]: 'ocupancy',
        [t(TK.surveys)]: 'surveys',
        [t(TK.googleReviews)]: 'googleReviews',
    }), [t])

    const cards = useMemo<CardDef[]>(() => [
        { tab: 'hours', icon: <AccessTimeIcon />, titleKey: TK.hours, descriptionKey: TK.hoursAnalyticsDescription },
        { tab: 'count', icon: <BarChartIcon />, titleKey: TK.count, descriptionKey: TK.countAnalyticsDescription },
        { tab: 'retention', icon: <PeopleIcon />, titleKey: TK.retention, descriptionKey: TK.retentionAnalyticsDescription },
        { tab: 'cancellations', icon: <EventBusyIcon />, titleKey: TK.cancellations, descriptionKey: TK.cancellationsAnalyticsDescription },
        { tab: 'ocupancy', icon: <PieChartIcon />, titleKey: TK.ocupancy, descriptionKey: TK.ocupancyAnalyticsDescription },
        { tab: 'surveys', icon: <PollIcon />, titleKey: TK.surveys, descriptionKey: TK.surveysAnalyticsDescription, hidden: !features.includes(FeatureEnum.Surveys) },
        { tab: 'googleReviews', icon: <StarIcon />, titleKey: TK.googleReviews, descriptionKey: TK.googleReviewsDescription },
        { icon: <HourglassEmptyIcon />, titleKey: TK.waitingLists, descriptionKey: TK.waitingListsAnalyticsDescription, externalPath: `${paths.waitingLists()}?tab=insights&dateType=month` },
    ], [features])

    if (showHome) {
        return (
            <TitledContainer id='analytics' title={t(TK.analytics)}>
                <div className='analytics-home-grid'>
                    {cards.filter(c => !c.hidden).map(card => (
                        <div
                            key={card.tab ?? card.externalPath}
                            className='analytics-home-card'
                            onClick={() => selectTab(card)}
                        >
                            <div className='analytics-card-icon-wrap'>{card.icon}</div>
                            <div className='analytics-card-title'>{t(card.titleKey)}</div>
                            <div className='analytics-card-description'>{t(card.descriptionKey)}</div>
                            <span className='analytics-card-arrow'><ChevronRightIcon fontSize='small' /></span>
                        </div>
                    ))}
                </div>
            </TitledContainer>
        )
    }

    return (
        <TitledContainer id='analytics' title={t(TK.analytics)}>
            <BackButton onClick={() => setShowHome(true)}>
                <ArrowBackIcon style={{fontSize: '1rem'}} />
                {t(TK.analytics)}
            </BackButton>

            <div style={{display: 'flex', gap: 'var(--spacer-XS)', flexDirection: 'column'}}>
                <AutocompleteTextField
                    label={t(TK.analytics)}
                    options={[
                        t(TK.hours),
                        t(TK.count),
                        t(TK.retention),
                        t(TK.cancellations),
                        t(TK.ocupancy),
                        ...(features.includes(FeatureEnum.Surveys) ? [t(TK.surveys)] : []),
                        t(TK.googleReviews),
                    ]}
                    value={t(tab as TK)}
                    onValueChanged={(tabText: string) => {
                        const newTab = tabMap[tabText]
                        setTab(newTab)
                        if (newTab !== 'surveys' && newTab !== 'googleReviews') setNeedLoad(true)
                        if (newTab === 'retention') setupRetention()
                    }}
                />

                <DateRangeSelector
                    style={{flexGrow: 1}}
                    onRangeChanged={setSelectedWindow}
                    defaultType={defaultIntervalType}
                    defaultStartDay={defaultIntervalStartDay}
                    defaultEndDay={defaultIntervalEndDay}
                    defaultMonth={defaultIntervalMonth}
                />
            </div>

            {tab === 'surveys' && features.includes(FeatureEnum.Surveys)
                ? <div style={{marginTop: 'var(--spacer-M)'}}><AnalyticsSurveys window={selectedWindow} /></div>
                : tab === 'googleReviews'
                ? <div style={{marginTop: 'var(--spacer-M)'}}><AnalyticsGoogleReviews window={selectedWindow} /></div>
                : isLoading
                ? <div style={{textAlign: 'center', height: '60vh', marginTop: 'var(--spacer-M)'}}><Spinner size='sm'/></div>
                : needLoad
                ? <div style={{textAlign: 'center', height: '60vh', marginTop: 'var(--spacer-M)'}}><AppButton color='success' disabled={isLoading} onClick={handleGo}>{t(TK.load)}</AppButton></div>
                : <>
                    {tab === 'count' || tab === 'hours'
                        ? <AnalyticsHours window={window} appointments={appointments} metric={tab} />
                        : tab === 'retention'
                        ? <AnalyticsRetention window={window} appointments={appointments} />
                        : tab === 'cancellations'
                        ? <AnalyticsCancellations appointments={appointments} cancellations={cancellations} />
                        : tab === 'ocupancy'
                        ? <AnalyticsOcupancy window={window} appointments={appointments} />
                        : <></>
                    }
                </>
            }
        </TitledContainer>
    )
}
